/**
 * QueteForm — modale d'enregistrement d'une quête de culte (3 sections).
 * Lignes dynamiques (ajout/retrait), suggestions cliquables, total live.
 */
import { useState, useEffect, useMemo, useCallback } from "react";
import Modal from "./Modal";
import Input from "./Input";
import Select from "./Select";
import Button from "./Button";
import { colors, fonts } from "../styles/tokens";
import { evenementsApi } from "../services/api";

const SUGGESTIONS = [
  "Tronc principal",
  "Tronc sortie",
  "Collecte spéciale",
  "Dîmes enveloppes",
  "Offrandes jeunesse",
  "Mission",
];

const DEVISES = [
  { value: "XOF", label: "XOF (F CFA Ouest)" },
  { value: "XAF", label: "XAF (F CFA Centre)" },
  { value: "EUR", label: "EUR (€)" },
  { value: "CAD", label: "CAD ($)" },
  { value: "CHF", label: "CHF" },
  { value: "USD", label: "USD ($)" },
];

const today = () => new Date().toISOString().slice(0, 10);

/** Devise par défaut selon le pays de l'église. */
function defaultDevise(pays) {
  if (!pays) return "XOF";
  const p = pays.toLowerCase();
  if (["côte d'ivoire", "cote d'ivoire", "sénégal", "senegal", "burkina faso", "mali", "togo", "bénin", "benin", "niger"].some(c => p.includes(c))) return "XOF";
  if (["cameroun", "gabon", "tchad", "congo", "centrafrique"].some(c => p.includes(c))) return "XAF";
  if (p.includes("suisse")) return "CHF";
  if (p.includes("canada")) return "CAD";
  return "EUR";
}

const EMPTY = {
  evenement_id: "",
  eglise_id: "",
  date_culte: today(),
  devise: "XOF",
  compte_par: "",
  note: "",
};

export default function QueteForm({
  open,
  onClose,
  onSubmit,
  onDelete,
  eglises = [],
  initial = null,             // mode édition si fourni
  prefillEvenement = null,    // événement pré-sélectionné (depuis page Événements)
}) {
  const [form, setForm] = useState(EMPTY);
  const [lignes, setLignes] = useState([{ libelle: "", montant: "" }]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [globalError, setGlobalError] = useState(null);
  const [eventsByEglise, setEventsByEglise] = useState({});

  // Reset / pré-remplit à chaque ouverture
  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        evenement_id: initial.evenement_id ? String(initial.evenement_id) : "",
        eglise_id: initial.eglise_id ? String(initial.eglise_id) : "",
        date_culte: initial.date_culte || today(),
        devise: initial.devise || "XOF",
        compte_par: initial.compte_par || "",
        note: initial.note || "",
      });
      setLignes(
        (initial.lignes || []).map((l) => ({
          libelle: l.libelle || "",
          montant: l.montant != null ? String(l.montant) : "",
        }))
      );
    } else if (prefillEvenement) {
      const eg = eglises.find((e) => e.id === prefillEvenement.eglise_id);
      setForm({
        ...EMPTY,
        evenement_id: String(prefillEvenement.id),
        eglise_id: String(prefillEvenement.eglise_id),
        date_culte: (prefillEvenement.date_debut || "").slice(0, 10) || today(),
        devise: defaultDevise(eg?.pays),
      });
      setLignes([{ libelle: "Tronc principal", montant: "" }]);
    } else {
      setForm({ ...EMPTY });
      setLignes([{ libelle: "Tronc principal", montant: "" }]);
    }
    setErrors({});
    setGlobalError(null);
  }, [open, initial, prefillEvenement, eglises]);

  // Charge les événements de type "culte" de l'église choisie
  useEffect(() => {
    if (!open || !form.eglise_id) return;
    if (eventsByEglise[form.eglise_id]) return;
    evenementsApi.list({ eglise_id: form.eglise_id, type: "culte" })
      .then(({ data }) => setEventsByEglise((prev) => ({ ...prev, [form.eglise_id]: data || [] })))
      .catch(() => setEventsByEglise((prev) => ({ ...prev, [form.eglise_id]: [] })));
  }, [open, form.eglise_id, eventsByEglise]);

  const set = (key) => (e) => {
    const value = e?.target ? e.target.value : e;
    if (key === "eglise_id") {
      // Met à jour la devise selon le pays de l'église
      const eg = eglises.find((x) => String(x.id) === String(value));
      setForm((prev) => ({
        ...prev,
        eglise_id: value,
        evenement_id: "",
        devise: prev.devise === EMPTY.devise || !prev.devise ? defaultDevise(eg?.pays) : prev.devise,
      }));
    } else if (key === "evenement_id") {
      // Pré-remplit la date de culte depuis l'événement choisi
      const evt = (eventsByEglise[form.eglise_id] || []).find((x) => String(x.id) === String(value));
      setForm((prev) => ({
        ...prev,
        evenement_id: value,
        date_culte: evt ? (evt.date_debut || "").slice(0, 10) : prev.date_culte,
      }));
    } else {
      setForm((prev) => ({ ...prev, [key]: value }));
    }
    setErrors((prev) => ({ ...prev, [key]: null }));
  };

  // ─── Lignes dynamiques ───
  const addLigne = (libelle = "") => {
    setLignes((prev) => [...prev, { libelle, montant: "" }]);
  };
  const removeLigne = (idx) => {
    setLignes((prev) => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);
  };
  const updateLigne = (idx, key, value) => {
    setLignes((prev) => prev.map((l, i) => i === idx ? { ...l, [key]: value } : l));
  };

  // Total live
  const total = useMemo(
    () => lignes.reduce((s, l) => s + (parseFloat(l.montant) || 0), 0),
    [lignes]
  );

  const fmtTotal = useCallback(() => {
    const sym = { EUR: "€", XOF: "F CFA", XAF: "F CFA", CAD: "$", CHF: "CHF", USD: "$" }[form.devise] || form.devise;
    return `${total.toLocaleString("fr-FR", { maximumFractionDigits: 2 })} ${sym}`;
  }, [total, form.devise]);

  // ─── Validation + soumission ───
  const validate = () => {
    const errs = {};
    if (!form.eglise_id)  errs.eglise_id = "Veuillez sélectionner une église";
    if (!form.date_culte) errs.date_culte = "Date requise";
    if (form.date_culte > today()) errs.date_culte = "La date ne peut pas être dans le futur";

    const valides = lignes.filter((l) => l.libelle.trim() && parseFloat(l.montant) > 0);
    if (valides.length === 0) errs.lignes = "Au moins une ligne avec un montant strictement positif est requise";

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setGlobalError(null);
    if (!validate()) return;

    setSubmitting(true);
    try {
      const payload = {
        eglise_id: Number(form.eglise_id),
        evenement_id: form.evenement_id ? Number(form.evenement_id) : null,
        date_culte: form.date_culte,
        devise: form.devise,
        compte_par: form.compte_par.trim() || null,
        note: form.note.trim() || null,
        lignes: lignes
          .filter((l) => l.libelle.trim() && parseFloat(l.montant) >= 0)
          .map((l, idx) => ({
            libelle: l.libelle.trim(),
            montant: parseFloat(l.montant) || 0,
            ordre: idx,
          })),
      };
      const result = await onSubmit(payload, { total });
      if (result?.ok === false) {
        setGlobalError(result.error || "Erreur lors de l'enregistrement");
      }
    } catch (err) {
      setGlobalError(err.message || "Erreur inattendue");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!initial || !onDelete) return;
    if (!window.confirm(`Supprimer la quête du ${initial.date_culte} (total ${initial.total} ${initial.devise}) ?`)) return;
    setSubmitting(true);
    try {
      await onDelete(initial);
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Options ───
  const eglisesOptions = eglises.map((e) => ({
    value: String(e.id),
    label: `${e.nom}${e.ville ? ` — ${e.ville}` : ""}`,
  }));
  const evtOptions = (eventsByEglise[form.eglise_id] || []).map((e) => ({
    value: String(e.id),
    label: `${e.titre} — ${new Date(e.date_debut).toLocaleDateString("fr-FR")}`,
  }));

  return (
    <Modal
      open={open}
      onClose={submitting ? undefined : onClose}
      title={initial ? "Modifier la quête" : "Nouvelle quête de culte"}
      subtitle={initial
        ? `Quête du ${new Date(initial.date_culte).toLocaleDateString("fr-FR")}`
        : "Recette globale du culte avec ses différentes collectes"}
      size="lg"
      footer={
        <>
          {initial && onDelete && (
            <Button variant="danger" onClick={handleDelete} disabled={submitting} style={{ marginRight: "auto" }}>
              🗑 Supprimer
            </Button>
          )}
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Annuler</Button>
          <Button onClick={handleSubmit} loading={submitting} disabled={submitting}>
            {initial ? "Sauvegarder" : "Enregistrer la quête"}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        {globalError && (
          <div style={{
            background: "rgba(224,91,106,0.1)",
            border: `1px solid ${colors.rose}`,
            color: colors.rose,
            padding: "10px 14px",
            borderRadius: "10px",
            fontSize: "13px",
          }}>
            {globalError}
          </div>
        )}

        {/* SECTION 1 — Informations générales */}
        <Section title="Informations générales">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "12px" }}>
            <Select
              label="Église"
              value={form.eglise_id}
              onChange={set("eglise_id")}
              options={eglisesOptions}
              required
              error={errors.eglise_id}
              placeholder="Sélectionnez une église..."
            />
            <Select
              label="Événement (culte) lié"
              value={form.evenement_id}
              onChange={set("evenement_id")}
              options={evtOptions}
              placeholder={
                !form.eglise_id ? "Sélectionnez d'abord une église"
                : evtOptions.length === 0 ? "Aucun culte trouvé"
                : "Aucun (date libre)"
              }
              disabled={!form.eglise_id}
            />
            <Input
              label="Date du culte"
              type="date"
              value={form.date_culte}
              onChange={set("date_culte")}
              required
              error={errors.date_culte}
              max={today()}
            />
            <Select
              label="Devise"
              value={form.devise}
              onChange={set("devise")}
              options={DEVISES}
              placeholder={null}
            />
          </div>
        </Section>

        {/* SECTION 2 — Lignes de collecte */}
        <Section
          title="Collectes"
          right={
            <Button size="sm" variant="ghost" onClick={() => addLigne()}>
              + Ajouter une ligne
            </Button>
          }
        >
          {/* Suggestions cliquables */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "12px" }}>
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => {
                  // Cherche une ligne vide libre ; sinon en ajoute une
                  const idx = lignes.findIndex((l) => !l.libelle.trim());
                  if (idx >= 0) {
                    updateLigne(idx, "libelle", s);
                  } else {
                    addLigne(s);
                  }
                }}
                style={{
                  fontSize: "12px",
                  padding: "5px 10px",
                  borderRadius: "999px",
                  background: colors.bg,
                  color: colors.textMuted,
                  border: `1px solid ${colors.border}`,
                  transition: "all 120ms",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = colors.goldDim; e.currentTarget.style.color = colors.gold; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = colors.bg; e.currentTarget.style.color = colors.textMuted; }}
              >
                + {s}
              </button>
            ))}
          </div>

          {/* Liste des lignes */}
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {lignes.map((l, idx) => (
              <div
                key={idx}
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(200px, 1fr) 140px 36px",
                  gap: "8px",
                  alignItems: "center",
                }}
              >
                <input
                  value={l.libelle}
                  onChange={(e) => updateLigne(idx, "libelle", e.target.value)}
                  placeholder="Libellé (ex : Tronc principal)"
                  style={lineInputStyle}
                  onFocus={(e) => { e.target.style.borderColor = colors.gold; }}
                  onBlur={(e)  => { e.target.style.borderColor = colors.border; }}
                />
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={l.montant}
                  onChange={(e) => updateLigne(idx, "montant", e.target.value)}
                  placeholder="Montant"
                  style={{ ...lineInputStyle, textAlign: "right", fontVariantNumeric: "tabular-nums" }}
                  onFocus={(e) => { e.target.style.borderColor = colors.gold; }}
                  onBlur={(e)  => { e.target.style.borderColor = colors.border; }}
                />
                <button
                  type="button"
                  onClick={() => removeLigne(idx)}
                  disabled={lignes.length === 1}
                  title="Supprimer la ligne"
                  style={{
                    background: "transparent",
                    border: `1px solid ${colors.border}`,
                    borderRadius: "8px",
                    height: 40,
                    color: lignes.length === 1 ? colors.textDim : colors.rose,
                    cursor: lignes.length === 1 ? "not-allowed" : "pointer",
                    fontSize: "16px",
                  }}
                >
                  🗑
                </button>
              </div>
            ))}
          </div>

          {/* Total */}
          <div
            style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              marginTop: "12px",
              padding: "12px 16px",
              borderTop: `1px solid ${colors.border}`,
              background: colors.goldDim,
              borderRadius: "10px",
            }}
          >
            <span style={{ fontSize: "13px", color: colors.textMuted, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase" }}>
              Total
            </span>
            <span style={{
              fontFamily: fonts.heading,
              fontSize: "26px",
              fontWeight: 700,
              color: total > 0 ? colors.gold : colors.textMuted,
            }}>
              {fmtTotal()} {total > 0 && <span style={{ color: colors.green }}>✓</span>}
            </span>
          </div>

          {errors.lignes && (
            <div style={{ fontSize: "12px", color: colors.rose, marginTop: "8px" }}>
              {errors.lignes}
            </div>
          )}
        </Section>

        {/* SECTION 3 — Comptage */}
        <Section title="Comptage">
          <Input
            label="Compté par"
            value={form.compte_par}
            onChange={set("compte_par")}
            placeholder="Ex : Diacre Amara + Sœur Fatou"
            icon="👥"
          />
          <label style={{ display: "flex", flexDirection: "column", gap: "6px", fontFamily: fonts.body, marginTop: "12px" }}>
            <span style={{ fontSize: "13px", fontWeight: 500, color: colors.textMuted }}>Note</span>
            <textarea
              value={form.note}
              onChange={set("note")}
              rows={2}
              placeholder="Précision (incidents, présence d'invités, etc.)"
              style={{
                background: colors.bg,
                color: colors.text,
                border: `1px solid ${colors.border}`,
                borderRadius: "10px",
                padding: "12px 14px",
                fontSize: "14px",
                outline: "none",
                fontFamily: fonts.body,
                resize: "vertical",
                minHeight: "60px",
              }}
              onFocus={(e) => { e.target.style.borderColor = colors.gold; }}
              onBlur={(e)  => { e.target.style.borderColor = colors.border; }}
            />
          </label>
        </Section>

        <button type="submit" hidden />
      </form>
    </Modal>
  );
}

const lineInputStyle = {
  background: colors.bg,
  color: colors.text,
  border: `1px solid ${colors.border}`,
  borderRadius: "10px",
  padding: "10px 14px",
  fontSize: "14px",
  outline: "none",
  fontFamily: fonts.body,
};

function Section({ title, right, children }) {
  return (
    <div>
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        marginBottom: "10px",
      }}>
        <span style={{
          fontSize: "11px",
          fontWeight: 700,
          color: colors.gold,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
        }}>
          {title}
        </span>
        {right}
      </div>
      {children}
    </div>
  );
}
